/**
 * Gate 2 execution scope + scorecard — shared metrics table (Figma).
 * Used by ExecutionScopeStep and QualityScorecardStep so typography matches.
 */

import {
  GATE2_BTN_FOCUS_SX,
  GATE2_FOCUS_RING_SX,
  GATE2_TOGGLE_FOCUS_SX,
} from "./gate2Tokens";

export const SCOPE_TABLE_BORDER = "1px solid #E6E6E6";

/** Checkbox cells in scope / scorecard tables */
export const GATE2_CHECKBOX_FOCUS_SX = {
  "&:focus": { outline: "none" },
  "&.Mui-focusVisible": {
    outline: "none",
    borderRadius: "4px",
    ...GATE2_FOCUS_RING_SX,
  },
};

/** Highlight row when a cell control (checkbox, link, button) has keyboard focus */
export const GATE2_TABLE_BODY_ROW_FOCUS_SX = {
  "&:focus-within": {
    outline: "none",
  },
  "&:focus-within .MuiTableCell-root": {
    bgcolor: "#F8FAFC",
  },
  "&:focus-within .MuiTableCell-root:first-of-type": {
    boxShadow: "inset 3px 0 0 #005BF0",
  },
};

export const GATE2_TABLE_LINK_FOCUS_SX = {
  ...GATE2_BTN_FOCUS_SX,
  borderRadius: "2px",
  "&:focus-visible:not(:active)": {
    ...GATE2_FOCUS_RING_SX,
    borderRadius: "2px",
  },
};

export const GATE2_PAGINATION_FOCUS_SX = {
  "& .MuiPaginationItem-root": {
    "&:focus": { outline: "none" },
    "&.Mui-focusVisible": {
      outline: "none",
      ...GATE2_FOCUS_RING_SX,
    },
  },
};

export const GATE2_OUTLINED_INPUT_FOCUS_SX = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor: "#005BF0 !important",
      borderWidth: "2px",
    },
  },
};

export const GATE2_DETAIL_TABLE_SX = {
  borderCollapse: "collapse",
  tableLayout: "fixed",
  width: "100%",
  "& .MuiTableBody-root .MuiTableRow-root": GATE2_TABLE_BODY_ROW_FOCUS_SX,
};

export const SCOPE_TABLE_WRAP_SX = {
  width: "100%",
  overflowX: "auto",
  boxSizing: "border-box",
  border: SCOPE_TABLE_BORDER,
  borderRadius: "8px",
  overflow: "hidden",
};

export const SCOPE_TABLE_SX = {
  width: "100%",
  minWidth: 724,
  borderCollapse: "collapse",
  tableLayout: "fixed",
  "& .MuiTableCell-root": {
    bgcolor: "#FFFFFF",
  },
  "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
    borderBottom: "none",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    borderBottom: SCOPE_TABLE_BORDER,
  },
  "& .MuiTableBody-root .MuiTableRow-root": GATE2_TABLE_BODY_ROW_FOCUS_SX,
};

export const SCOPE_TABLE_CELL_ROW_SX = {
  borderBottom: SCOPE_TABLE_BORDER,
  borderRight: "none",
};

export const TABLE_HEAD_CELL_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 16,
  fontWeight: 700,
  lineHeight: "100%",
  color: "#383838",
  bgcolor: "#FFFFFF",
  py: 1.5,
  px: 2,
  whiteSpace: "nowrap",
  ...SCOPE_TABLE_CELL_ROW_SX,
};

export const TABLE_BODY_CELL_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
  color: "#383838",
  py: 1.75,
  px: 2,
  verticalAlign: "middle",
  bgcolor: "#FFFFFF",
  ...SCOPE_TABLE_CELL_ROW_SX,
};

export const SCOPE_TABLE_CHECKBOX_CELL_SX = {
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  width: 44,
  maxWidth: 44,
  minWidth: 44,
  p: 0,
  pl: "12px !important",
  pr: "0 !important",
  verticalAlign: "middle",
};

export const SCOPE_TABLE_METRIC_NAME_CELL_SX = {
  pl: "8px !important",
  pr: 2,
};

export const SCOPE_TABLE_METRIC_NAME_HEAD_SX = {
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

export const SCOPE_TABLE_METRIC_NAME_VALUE_SX = {
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

/** Name column — clip inner ellipsis text (pair with explicit col width or flex col + maxWidth: 0). */
export const SCOPE_TABLE_TRUNCATE_NAME_CELL_SX = {
  overflow: "hidden",
};

export const SCOPE_TABLE_THRESHOLD_HEAD_SX = {
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

export const SCOPE_TABLE_THRESHOLD_VALUE_SX = {
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#212121",
};

export const SCOPE_TABLE_TEST_CASES_HEAD_SX = {
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

export const SCOPE_TABLE_TEST_CASES_VALUE_SX = {
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#212121",
};

export const SCOPE_TABLE_ACTIONS_HEAD_SX = {
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

/** Figma 5618:34776 — metrics table header row (#FAFAFA, 13px bold, 44px tall). */
export const SCOPE_METRICS_TABLE_HEAD_CELL_SX = {
  ...TABLE_HEAD_CELL_SX,
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "100%",
  color: "#383838",
  bgcolor: "#FAFAFA",
  height: 44,
  py: 0,
  px: 2,
  boxSizing: "border-box",
};

export const SCOPE_METRICS_TABLE_BODY_CELL_SX = {
  ...TABLE_BODY_CELL_SX,
  fontSize: 13,
  lineHeight: "100%",
  height: 56,
  py: 0,
  px: 2,
  boxSizing: "border-box",
  verticalAlign: "middle",
};

export const SCOPE_TABLE_ACTIONS_CELL_SX = {
  textAlign: "right",
  whiteSpace: "nowrap",
};

export const SCOPE_TABLE_THRESHOLD_CELL_SX = {
  width: 200,
  minWidth: 200,
  maxWidth: 200,
};

export const SCOPE_TABLE_TEST_CASES_CELL_SX = {
  width: 160,
  minWidth: 160,
  maxWidth: 160,
};

export const SCOPE_TABLE_ACTIONS_COL_SX = {
  width: 120,
  minWidth: 120,
  maxWidth: 120,
};

export const SCOPE_VIEW_DETAILS_LINK_SX = {
  ...GATE2_TABLE_LINK_FOCUS_SX,
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#005BF0",
  textUnderlineOffset: "2px",
  verticalAlign: "middle",
  cursor: "pointer",
  "&:hover": { color: "#005BF0" },
};

/** Figma — Select / Confirmed metrics card (Grey-20 border, 8px radius, 24px pad, 16px gap). */
export const SCOPE_METRICS_CARD_SX = {
  bgcolor: "#FFFFFF",
  border: "1px solid #E6E6E6",
  borderRadius: "8px",
  boxSizing: "border-box",
  width: "100%",
  maxWidth: "100%",
  p: "24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

/** Shared title + header row for scope-step connector cards (matches metrics card). */
export const SCOPE_CONNECTOR_CARD_TITLE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 20,
  fontWeight: 700,
  lineHeight: "normal",
  letterSpacing: 0,
  color: "#0F172A",
};

export const SCOPE_CONNECTOR_CARD_HEADER_ROW_SX = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: { xs: "stretch", md: "center" },
  justifyContent: "space-between",
  gap: 2,
};

export const SCOPE_CARD_TITLE_SX = SCOPE_CONNECTOR_CARD_TITLE_SX;
export const SCOPE_CARD_HEADER_ROW_SX = SCOPE_CONNECTOR_CARD_HEADER_ROW_SX;

/** Shared field labels — list + table (Figma Grey-50 #707070, 12px). */
export const SCOPE_METRICS_FIELD_LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#707070",
};

export const SCOPE_LIST_COL_LABEL_SX = SCOPE_METRICS_FIELD_LABEL_SX;

export const SCOPE_METRICS_NAME_VALUE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const SCOPE_LIST_METRIC_VALUE_SX = SCOPE_METRICS_NAME_VALUE_SX;

export const SCOPE_METRICS_THRESHOLD_VALUE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#212121",
};

export const SCOPE_LIST_THRESHOLD_VALUE_SX = SCOPE_METRICS_THRESHOLD_VALUE_SX;

export const SCOPE_METRICS_TEST_CASES_VALUE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#212121",
};

export const SCOPE_LIST_TEST_CASES_VALUE_SX = SCOPE_METRICS_TEST_CASES_VALUE_SX;

export const SCOPE_METRICS_BLOCKED_TEXT_SX = { color: "#94A3B8" };

export const SCOPE_METRICS_BLOCKED_ROW_OPACITY = 0.72;

/** Vertical stack of metric list rows (16px gap). */
export const SCOPE_LIST_STACK_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
};

/** Figma 5618:34728 — list row chrome (12px radius, 14×16 padding). */
export const SCOPE_LIST_ROW_BASE_SX = {
  width: "100%",
  maxWidth: "100%",
  minHeight: { xs: "auto", md: 69 },
  boxSizing: "border-box",
  pt: "14px",
  pr: "16px",
  pb: "14px",
  pl: "16px",
  bgcolor: "#FFFFFF",
  border: "1px solid #E6E6E6",
  borderRadius: "12px",
  overflow: "hidden",
  "&:focus-within": {
    outline: "none",
    borderColor: "#005BF0",
    boxShadow: "inset 3px 0 0 #005BF0",
    bgcolor: "#F8FAFC",
  },
};

export const SCOPE_LIST_ROW_LAYOUT_SX = {
  display: { xs: "flex", md: "grid" },
  flexDirection: { xs: "column", md: "unset" },
  alignItems: { xs: "stretch", md: "center" },
  columnGap: "24px",
  rowGap: { xs: "16px", md: 0 },
};

export const scopeListRowGridColumns = (confirmationMode) =>
  confirmationMode
    ? "minmax(0, 354px) minmax(200px, 1fr) 120px"
    : "44px minmax(0, 354px) minmax(200px, 1fr) 120px";

export const SCOPE_LIST_CELL_CHECKBOX_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  flexShrink: 0,
};

export const SCOPE_LIST_CELL_METRIC_SX = {
  minWidth: 0,
  maxWidth: { md: 354 },
};

export const SCOPE_LIST_METRIC_STACK_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  minWidth: 0,
};

export const SCOPE_LIST_CELL_FIELDS_SX = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "flex-start" },
  gap: { xs: "16px", sm: "32px" },
  minWidth: 0,
};

export const SCOPE_LIST_FIELD_STACK_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: "0 0 auto",
  minWidth: { sm: 200 },
  maxWidth: { sm: 200 },
};

export const SCOPE_LIST_FIELD_TEST_CASES_SX = {
  ...SCOPE_LIST_FIELD_STACK_SX,
  minWidth: { sm: 160 },
  maxWidth: { sm: 160 },
};

export const SCOPE_LIST_CELL_ACTIONS_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: { xs: "flex-start", md: "flex-end" },
  flexShrink: 0,
  minWidth: { md: 120 },
};

/** @deprecated Use SCOPE_LIST_ROW_BASE_SX + SCOPE_LIST_ROW_LAYOUT_SX + scopeListRowGridColumns */
export const SCOPE_LIST_ROW_SX = {
  ...SCOPE_LIST_ROW_BASE_SX,
  ...SCOPE_LIST_ROW_LAYOUT_SX,
  gridTemplateColumns: { md: scopeListRowGridColumns(false) },
};

export const SCOPE_LIST_VIEW_TOGGLE_SX = {
  ...GATE2_TOGGLE_FOCUS_SX,
  p: 0,
  minWidth: 32,
  width: 32,
  height: 32,
  borderRadius: "4px !important",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
  "& svg path": { fill: "currentColor" },
  "& .MuiTouchRipple-root": { display: "none" },
  "&:hover": { bgcolor: "inherit" },
};

/** Figma — list view toggle (32×32, 16px icon). */
export const SCOPE_VIEW_TOGGLE_ICON_SIZE = 16;

/** Same chrome as Projects grid/table toggles — glyph icon + white tile / blue selected. */
export const SCOPE_VIEW_TOGGLE_SX = {
  ...SCOPE_LIST_VIEW_TOGGLE_SX,
  border: "1px solid #BBBBBB !important",
  bgcolor: "#FFFFFF",
  backgroundImage: "none",
  boxShadow: "none",
  color: "#555555",
  "& svg path": { fill: "currentColor" },
  "&.Mui-selected": {
    bgcolor: "#005BF0",
    backgroundColor: "#005BF0",
    backgroundImage: "none",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.25)",
  },
  "&.Mui-selected:hover:not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#005BF0",
    backgroundColor: "#005BF0",
    backgroundImage: "none",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.25)",
  },
  "&.Mui-selected:focus:not(:active), &.Mui-selected:focus-visible:not(:active), &.Mui-selected.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important",
  },
  "&.Mui-selected:hover:focus:not(:active), &.Mui-selected:hover:focus-visible:not(:active), &.Mui-selected:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important",
  },
  "&.Mui-selected:active, &.Mui-selected:hover:active, &.Mui-selected:focus:active, &.Mui-selected.Mui-focusVisible:active": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage:
      "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0) !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5) !important",
  },
  "&.Mui-disabled, &.Mui-selected.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    color: "#BBBBBB",
    border: "1px solid #E6E6E6",
    borderColor: "#E6E6E6 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.25)",
    opacity: 1,
  },
  "&:not(.Mui-selected):hover": { bgcolor: "#FFFFFF", backgroundColor: "#FFFFFF" },
};

export const SCOPE_LIST_VIEW_TOGGLE_BTN_SX = SCOPE_VIEW_TOGGLE_SX;

/** Figma — table view toggle: 32×32 button, 18×18 icon (7px inset when centered). */
export const SCOPE_TABLE_VIEW_TOGGLE_ICON_SIZE = 18;

export const SCOPE_TABLE_VIEW_TOGGLE_SX = {
  ...SCOPE_LIST_VIEW_TOGGLE_SX,
  border: "1px solid #BBBBBB !important",
  bgcolor: "#FFFFFF",
  backgroundImage: "none",
  boxShadow: "none",
  color: "#555555",
  "& svg path": { fill: "currentColor" },
  "&.Mui-selected": {
    bgcolor: "#005BF0",
    backgroundColor: "#005BF0",
    backgroundImage: "none",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.25)",
  },
  "&.Mui-selected:hover:not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#005BF0",
    backgroundColor: "#005BF0",
    backgroundImage: "none",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.25)",
  },
  "&.Mui-selected:focus:not(:active), &.Mui-selected:focus-visible:not(:active), &.Mui-selected.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important",
  },
  "&.Mui-selected:hover:focus:not(:active), &.Mui-selected:hover:focus-visible:not(:active), &.Mui-selected:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important",
  },
  "&.Mui-selected:active, &.Mui-selected:hover:active, &.Mui-selected:focus:active, &.Mui-selected.Mui-focusVisible:active": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage:
      "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0) !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5) !important",
  },
  "&.Mui-disabled, &.Mui-selected.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    color: "#BBBBBB",
    border: "1px solid #E6E6E6",
    borderColor: "#E6E6E6 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.25)",
    opacity: 1,
  },
  "&:not(.Mui-selected):hover": { bgcolor: "#FFFFFF", backgroundColor: "#FFFFFF" },
};
